import React, { useState } from 'react';
import { Box, IconButton, Paper, Tooltip, Badge, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import {
  Add as ZoomInIcon,
  Remove as ZoomOutIcon,
  MyLocation as RecenterIcon,
  FilterList as FilterIcon,
  Route as RouteIcon,
  Layers as LayersIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Map as RoadmapIcon,
  Satellite as SatelliteIcon,
  Terrain as TerrainIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useMapContext } from '../../context/MapContext';

const MapControls = ({ 
  onShowFilters, 
  onShowRoutePlanner, 
  activeFilterCount = 0,
  hideMobileButtons = false,
  hideDesktopButtons = false,
}) => {
  const { map, zoomIn, zoomOut, recenterMap } = useMapContext();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mapTypeAnchor, setMapTypeAnchor] = useState(null);
  const [currentMapType, setCurrentMapType] = useState('roadmap');

  const handleMapTypeClick = (event) => {
    setMapTypeAnchor(event.currentTarget);
  };

  const handleMapTypeClose = () => {
    setMapTypeAnchor(null);
  };

  const handleMapTypeChange = (mapType) => {
    if (map) {
      map.setMapTypeId(mapType);
      setCurrentMapType(mapType);
    }
    handleMapTypeClose();
  };

  const toggleFullscreen = () => {
    const mapContainer = document.querySelector('.map-container') || document.body;
    
    if (!isFullscreen) {
      // Enter fullscreen
      if (mapContainer.requestFullscreen) {
        mapContainer.requestFullscreen();
      } else if (mapContainer.webkitRequestFullscreen) {
        mapContainer.webkitRequestFullscreen();
      } else if (mapContainer.mozRequestFullScreen) {
        mapContainer.mozRequestFullScreen();
      } else if (mapContainer.msRequestFullscreen) {
        mapContainer.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen changes
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const mapTypes = [
    { id: 'roadmap', label: 'Roadmap', icon: <RoadmapIcon /> },
    { id: 'satellite', label: 'Satellite', icon: <SatelliteIcon /> },
    { id: 'hybrid', label: 'Hybrid', icon: <SatelliteIcon /> },
    { id: 'terrain', label: 'Terrain', icon: <TerrainIcon /> },
  ];

  const allButtons = [
    {
      icon: <FilterIcon />,
      label: 'Filters',
      onClick: onShowFilters,
      badge: activeFilterCount,
      showOnMobile: true,
      showOnDesktop: !hideDesktopButtons,
    },
    {
      icon: <RouteIcon />,
      label: 'Plan Route',
      onClick: onShowRoutePlanner,
      showOnMobile: true,
      showOnDesktop: !hideDesktopButtons,
    },
    {
      icon: <LayersIcon />,
      label: 'Map Type',
      onClick: handleMapTypeClick,
      showOnMobile: true,
      showOnDesktop: true,
    },
    {
      icon: isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />,
      label: isFullscreen ? 'Exit Fullscreen' : 'Fullscreen',
      onClick: toggleFullscreen,
      showOnMobile: true,
      showOnDesktop: true,
    },
    {
      icon: <RecenterIcon />,
      label: 'Recenter',
      onClick: recenterMap,
      showOnMobile: true,
      showOnDesktop: true,
    },
    {
      icon: <ZoomInIcon />,
      label: 'Zoom In',
      onClick: zoomIn,
      showOnMobile: true,
      showOnDesktop: true,
    },
    {
      icon: <ZoomOutIcon />,
      label: 'Zoom Out',
      onClick: zoomOut,
      showOnMobile: true,
      showOnDesktop: true,
    },
  ];

  const controlButtons = allButtons.filter(button => {
    // Always show all buttons (visibility handled by CSS)
    return true;
  });

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {controlButtons.map((button, index) => (
          <motion.div
            key={button.label}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: button.showOnDesktop ? 'block' : 'none',
            }}
            sx={{
              display: {
                xs: button.showOnMobile ? 'block' : 'none',
                md: button.showOnDesktop ? 'block' : 'none',
              }
            }}
          >
            <Tooltip title={button.label} placement="left">
              <Paper
                elevation={3}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: 'white',
                  display: {
                    xs: button.showOnMobile ? 'block' : 'none',
                    md: button.showOnDesktop ? 'block' : 'none',
                  }
                }}
              >
                <Badge
                  badgeContent={button.badge}
                  color="primary"
                  invisible={!button.badge}
                >
                  <IconButton
                    onClick={button.onClick}
                    sx={{
                      width: 48,
                      height: 48,
                      color: 'black',
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: 'white',
                        color: 'grey.600',
                      },
                    }}
                  >
                    {button.icon}
                  </IconButton>
                </Badge>
              </Paper>
            </Tooltip>
          </motion.div>
        ))}
      </Box>

      {/* Map Type Menu */}
      <Menu
        anchorEl={mapTypeAnchor}
        open={Boolean(mapTypeAnchor)}
        onClose={handleMapTypeClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: 2,
            minWidth: 180,
          },
        }}
      >
        {mapTypes.map((type) => (
          <MenuItem
            key={type.id}
            onClick={() => handleMapTypeChange(type.id)}
            selected={currentMapType === type.id}
            sx={{
              py: 1.5,
              '&.Mui-selected': {
                backgroundColor: 'primary.lighter',
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: currentMapType === type.id ? 'primary.main' : 'inherit' }}>
              {type.icon}
            </ListItemIcon>
            <ListItemText 
              primary={type.label}
              primaryTypographyProps={{
                fontWeight: currentMapType === type.id ? 'bold' : 'normal',
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default MapControls;
